import { useState } from "react";
import {
  deriveTaskLifecycle,
  getTaskLifecycleLabel,
  taskLifecycleRank,
  type TaskLifecycle,
  type TaskLifecycleSource,
} from "@shared/taskLifecycle";

export function statusRank(task: TaskLifecycleSource) {
  return taskLifecycleRank(deriveTaskLifecycle(task));
}

export function sortTasksByStatus<T extends TaskLifecycleSource>(tasks: T[]) {
  return [...tasks].sort((a, b) => statusRank(a) - statusRank(b));
}

export function shouldShowStatusDivider<T extends TaskLifecycleSource>(
  tasks: T[],
  index: number,
) {
  return index === 0 || deriveTaskLifecycle(tasks[index - 1]) !== deriveTaskLifecycle(tasks[index]);
}

export function statusCount<T extends TaskLifecycleSource>(tasks: T[], status: TaskLifecycle) {
  return tasks.filter(task => deriveTaskLifecycle(task) === status).length;
}

export function useCollapsedStatuses() {
  const [collapsed, setCollapsed] = useState<Set<TaskLifecycle>>(() => new Set());
  function toggle(status: TaskLifecycle) {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  }
  return { collapsed, toggle };
}

export default function TaskStatusDivider({
  status,
  count,
  collapsed,
  onToggle,
}: {
  status: TaskLifecycle;
  count?: number;
  collapsed?: boolean;
  onToggle?: () => void;
}) {
  const label = getTaskLifecycleLabel(status);
  const content = (
    <>
      <span>{label}</span>
      {count !== undefined && (
        <span className="tabular-nums font-mono" aria-label={`${count} ${label.toLowerCase()} tasks`}>
          {count}
        </span>
      )}
    </>
  );

  if (onToggle) {
    return (
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        className="flex w-full items-center justify-between bg-muted/45 px-4 py-1.5 text-left text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground"
      >
        {content}
      </button>
    );
  }

  return (
    <div className="flex items-center justify-between bg-muted/45 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
      {content}
    </div>
  );
}
